import React, { useState } from "react";
import { Select } from "../../form-elements";
import DynamicForm, { OnFormSubmitFunc } from "../../form-elements/dynamic-form/dynamic-form";
import CompanyForms from "./company-forms";
import { TSelectItem } from "../../form-elements/select/select";
import FormBlockWrapper from "./forms-block-wrapper";
import { TAlertSummary } from "../../elements/alert-summary/alert-summary";
import {
  TPostCustomerServiceCompanyServiceNowParams,
  usePostCustomerServiceCompanyServiceNow,
} from "../../../react-query-queries/customer-service/use-post-customer-service-service-now";

type TCustomerServiceCompanyBlockProps = {
  title: string;
  preamble?: React.ReactNode;
};

type TCompanyFormKey = keyof typeof CompanyForms;

const CustomerServiceCompanyBlock = ({ title, preamble }: TCustomerServiceCompanyBlockProps) => {
  const [selectedForm, setSelectedForm] = useState<string | null>(Object.keys(CompanyForms)[0]);
  const [formStatus, setFormStatus] = useState<TAlertSummary>();
  const selectFormItems: Array<TSelectItem> = Object.entries(CompanyForms).map<TSelectItem>(([key, form]) => ({
    label: form.label,
    value: key,
  }));

  const formToRender = selectedForm ? CompanyForms[selectedForm as TCompanyFormKey] : null;

  const { mutateAsync: postForm, isPending } = usePostCustomerServiceCompanyServiceNow();

  const onPostForm: OnFormSubmitFunc = (formValues: Record<string, unknown>, { reset }) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { attachedFiles: files, termsAccepted: __, ...formValuesCopy } = formValues;

    let requestData: Record<string, unknown> = {};
    if (selectedForm === "addresschange") {
      requestData = {
        ...formValuesCopy,
        subCategory: "Address/Name Change",
        subject: `${formValuesCopy.subCategory}`,
      };
    } else {
      requestData = {
        ...formValuesCopy,
      };
    }

    const subCategory =
      typeof requestData.subCategory === "string" && requestData.subCategory.indexOf("#") > -1
        ? requestData.subCategory.split("#")[0]
        : requestData.subCategory;

    const request = {
      invoiceNumber: requestData.invoiceNumber as string,
      customerNumber: requestData.customerNumber as string,
      cardNumber: requestData.cardNumber as string,
      cardListType: requestData.cardListType as string,
      organisationNumber: requestData.organisationNumber as string,
      subCategory: subCategory as string,
      firstName: requestData.firstName as string,
      lastName: requestData.lastName as string,
      phone: requestData.phone as string,
      email: requestData.email as string,
      requestType: selectedForm ?? "",
      description: requestData.description as string,
      subject: requestData.subject as string,
      attachedFiles: files as Array<File>,
      termsAccepted: true,
    } satisfies TPostCustomerServiceCompanyServiceNowParams;

    postForm(request)
      .then(() => {
        setFormStatus({
          type: "success",
          heading: "Ditt ärende är skickat",
          messages: [
            {
              text: "Ditt supportärende är nu registrerat hos oss. Vi arbetar med att hantera alla inkommande ärenden och kommer att kontakta dig inom kort för att hjälpa dig vidare.",
            },
          ],
        });
        reset({ description: "" }, { keepDefaultValues: true });
      })
      .catch((error) => {
        setFormStatus({
          type: "error",
          heading: "Problem med bakomliggande system",
          messages: [
            {
              text: "Det gick tyvärr inte att skicka in ditt ärende. Om det fortsatt inte går kontakta kundservice@preem.se.",
            },
          ],
        });
        console.error(error);
      });
  };

  const hideSubmitButton = selectedForm === "edi";
  const dynamicFormKey = `${selectedForm}-${formStatus ? formStatus.toString() : "no-key"}`;

  return (
    <FormBlockWrapper>
      <div className={"text-max-width"}>
        <h3>{title}</h3>
        {preamble && <div>{preamble}</div>}
      </div>
      <div className={"form-max-width"}>
        <Select
          label={"Välj ärendetyp"}
          value={selectedForm}
          onChange={(value) => setSelectedForm(value)}
          items={selectFormItems}
        />
        {formToRender ? (
          <DynamicForm
            key={dynamicFormKey}
            form={formToRender.form}
            onFormSubmit={onPostForm}
            formStatus={formStatus}
            isPostingForm={isPending}
            hideSubmitButton={hideSubmitButton}
          />
        ) : (
          <p>Välj ärendetyp först</p>
        )}
      </div>
    </FormBlockWrapper>
  );
};

export default CustomerServiceCompanyBlock;
